import { Button, Flex, Text } from '@chakra-ui/react';
import s from './styles.module.scss';
import { useContext, useEffect, useMemo, useState } from 'react';
import { WalletContext } from '@/contexts/wallet-context';
import ProposalResult from '@/modules/AlphaPWA/DirectMessage/Chat/Proposal/Result';
import cx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import { requestReload, selectCommonReducer } from '@/state/common';
import { toast } from 'react-hot-toast';
import CAlphaGovernor from '@/contracts/alphaGovernor';
import { getProposalDetail } from '@/services/governor';
import moment from 'moment';
import { compareString, formatCurrency } from '@/utils';
import CPlayerShare from '@/contracts';
import { labelAmountOrNumberAdds } from '@/modules/AlphaPWA/Profiles/TradeKey/constants';
import { formatEther } from 'ethers/lib/utils';
import CTradeAPI from '@/services/classes/trade';
import BackIcon from '@/modules/AlphaPWA/OverlayPages/ReferralEarning/icons/BackIcon';
import { formatString } from '@/utils/format';
import { closeModal, openModal } from '@/state/modal';

export const CONFIRM_CANCEL_PROPOSAL = 'CONFIRM_CANCEL_PROPOSAL';

const VotingModal = ({ profile, proposal, onClose }: any) => {
  const { addressL2 } = useContext(WalletContext);
  const [submitting, setSubmitting] = useState(false);
  const needReload = useSelector(selectCommonReducer).needReload;
  const governorContract = new CAlphaGovernor();
  const cplayerShare = new CPlayerShare();
  const dispatch = useDispatch();
  const [proposalDetail, setProposalDetail] = useState<any>(null);
  const [voteValue, setVoteValue] = useState('');
  const [keyVote, setKeyVote] = useState<any>('0');
  const [proposalData, setProposalData] = useState<any>(null);
  const [forProfile, setForProfile] = useState<any>(null);
  const [isCanceling, setIsCaneling] = useState(false);

  const tradeAPI = new CTradeAPI();

  const allowCancel = useMemo(() => {
    return (
      compareString(profile.address, addressL2) &&
      moment().isBefore(moment(proposal?.vote_end_at)) &&
      proposal?.status !== 'cancel'
    );
  }, [proposal, profile, addressL2]);

  const allowVote = useMemo(() => {
    return (
      Number(keyVote) > 0.0000001 &&
      moment().isAfter(moment(proposal?.vote_start_at)) &&
      moment().isBefore(moment(proposal?.vote_end_at))
    );
  }, [proposal, keyVote]);

  useEffect(() => {
    if (proposalDetail?.proposal?.voted) {
      setVoteValue(proposalDetail?.proposal?.voted_support);
    }
  }, [JSON.stringify(proposalDetail)]);

  const isVoteFor = useMemo(() => {
    return !voteValue || compareString(voteValue, '1');
  }, [voteValue, JSON.stringify(proposalDetail)]);

  const isVoteAgainst = useMemo(() => {
    return !voteValue || compareString(voteValue, '0');
  }, [voteValue, JSON.stringify(proposalDetail)]);

  useEffect(() => {
    if (proposalData && proposalData.length > 0 && proposalData[2]) {
      getForProfile(proposalData[2]);
    }
  }, [proposalData]);

  const getForProfile = async (address: string) => {
    const profile = await tradeAPI.getUserProfile(address, addressL2);

    setForProfile(profile);
  };

  useEffect(() => {
    if (profile && proposal && addressL2) {
      fetchTreasury();
    }
  }, [profile, proposal, addressL2, needReload]);

  const fetchTreasury = async () => {
    try {
      const [propsal, pastVotes, data] = await Promise.all([
        getProposalDetail(proposal?.proposal_id, { address: addressL2 || '' }),
        cplayerShare.getPastVotes({
          token_address: profile?.tokenAddress,
          address: addressL2 || '',
          voteStartBlock: proposal?.vote_start,
        }),
        governorContract.getCreateProposalData(
          proposal.targets ? JSON.parse(proposal.targets)[0] : '',
          proposal.calldatas ? JSON.parse(proposal.calldatas)[0] : ''
        ),
      ]);

      setProposalDetail(propsal);
      setKeyVote(pastVotes);
      setProposalData(data);
    } catch (error) {}
  };

  const onSubmit = async (value: string) => {
    try {
      setSubmitting(true);
      setVoteValue(value);

      const tx = await governorContract.voteProposal({
        governorAddress: profile?.governorAddress,
        proposalId: proposal.proposal_id,
        value: value,
      });

      toast.success('Vote proposal successfully!');

      dispatch(requestReload());
    } catch (e) {
      console.log('Voting Proposal', e);
    } finally {
      setSubmitting(false);
    }
  };

  const onCancelProposol = async () => {
    handleCloseModal();
    try {
      setIsCaneling(true);
      await governorContract.creatorCancelProposal({
        governorAddress: profile?.governorAddress,
        calldatas: proposal?.calldatas,
        targets: proposal?.targets,
        values: proposal?.values,
        description: proposal?.description,
      });

      toast.success('Cancel proposal successfully!');
      dispatch(requestReload());
      onClose();
    } catch (e) {
      console.log('Voting Proposal', e);
    } finally {
      setIsCaneling(false);
    }
  };

  const handleCloseModal = () => {
    dispatch(closeModal({ id: CONFIRM_CANCEL_PROPOSAL }));
  };

  const getDescription = (data: any) => {
    try {
      if (data || data.description) {
        const _data = JSON.parse(data.description as string);
        return _data.desc;
      }
    } catch (error) {
      return data?.description || '';
    }
  };

  const getTitle = (data: any) => {
    try {
      if (data || data.title) {
        const _data = JSON.parse(data.description as string);
        return _data.title;
      }
    } catch (error) {
      return `Elect a Community Manager for ${profile?.twitterName}`;
    }
  };

  return (
    <Flex
      className={s.container}
      direction={'column'}
      justifyContent={'space-between'}
    >
      <Flex direction={'column'} p={4} gap={4}>
        <Flex direction={'column'} gap={4}>
          <Flex direction={'column'} fontWeight={'600'}>
            <Flex gap={2} justifyContent="space-between" alignItems="center">
              <Flex onClick={onClose}>
                <Flex ml="-6px">
                  <BackIcon />
                </Flex>
                <Text fontSize={'16px !important'}>
                  Proposal #{proposal?.id}
                </Text>
              </Flex>
              {allowCancel && (
                <Button
                  isDisabled={isCanceling || !allowCancel}
                  isLoading={isCanceling}
                  loadingText={'Canceling...'}
                  onClick={e => {
                    e.stopPropagation();
                    dispatch(
                      openModal({
                        id: CONFIRM_CANCEL_PROPOSAL,
                        theme: 'dark',
                        modalProps: {
                          centered: true,
                          zIndex: 9999999,
                        },
                        render: () => (
                          <ModalConfirm
                            handleCloseModal={handleCloseModal}
                            onCancel={onCancelProposol}
                          />
                        ),
                      })
                    );
                  }}
                  className={s.btnCancel}
                >
                  Cancel proposal
                </Button>
              )}
            </Flex>
            <Text
              fontSize={'20px !important'}
              fontWeight={600}
              mt="20px"
              pb="12px"
              borderBottom="1px solid #FFFFFF26"
            >
              {formatString(getTitle(proposal), 50)}
            </Text>
          </Flex>
          <Flex
            direction={'column'}
            gap={4}
            pb="12px"
            borderBottom="1px solid #FFFFFF26"
          >
            <Text className={s.proposer}>
              Proposer: <span>{proposal?.proposer?.twitter_name}</span>
            </Text>
            {proposalData && (
              <>
                <Text className={s.proposer}>
                  Amount:{' '}
                  <span>
                    {formatCurrency(
                      formatEther(proposalData[1]),
                      0,
                      0,
                      'TC',
                      true
                    )}{' '}
                    {profile?.twitterName} token
                    {labelAmountOrNumberAdds(
                      Number(formatEther(proposalData[1]))
                    )}
                  </span>
                </Text>
                <Text className={s.proposer}>
                  For: <span>{forProfile?.twitter_name}</span>
                </Text>
              </>
            )}
          </Flex>
        </Flex>
        <ProposalResult proposal={proposal} />
        <Flex direction={'column'} gap={2} mt={4}>
          <Text className={s.descTitle}>Description</Text>
          <Flex direction={'column'} gap={5} className={s.descContent}>
            <Text>{getDescription(proposal)}</Text>
          </Flex>
        </Flex>
      </Flex>

      {proposal?.status !== 'cancel' && (
        <Flex className={s.actionsWrapper} direction={'column'} gap={'10px'}>
          <Flex alignItems={'center'} gap={1}>
            <Text className={s.proposer}>
              <span>Your voting power: </span>
              {formatCurrency(keyVote)} key
              {labelAmountOrNumberAdds(Number(keyVote))}
            </Text>
          </Flex>
          <Flex gap={'20px'} alignItems={'center'}>
            {isVoteAgainst && (
              <Button
                isDisabled={submitting || !allowVote}
                isLoading={submitting}
                loadingText={'Voting...'}
                onClick={() => onSubmit('0')}
                className={cx(
                  s.btnAgainst,
                  proposalDetail?.proposal?.voted ? s.voted : ''
                )}
              >
                {proposalDetail?.proposal?.voted
                  ? 'You voted "Against"'
                  : 'Against'}
              </Button>
            )}
            {isVoteFor && (
              <Button
                isDisabled={submitting || !allowVote}
                isLoading={submitting}
                loadingText={'Voting...'}
                onClick={() => onSubmit('1')}
                className={cx(
                  s.btnFor,
                  proposalDetail?.proposal?.voted ? s.voted : ''
                )}
              >
                {proposalDetail?.proposal?.voted ? 'You voted "For"' : 'For'}
              </Button>
            )}
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};

export default VotingModal;

const ModalConfirm = ({
  handleCloseModal,
  onCancel,
}: {
  handleCloseModal: () => void;
  onCancel: () => void;
}) => {
  return (
    <div className={s.wrapModal}>
      <div className={s.body}>Do you want to cancel this proposal?</div>
      <div className={s.footer}>
        <Button className={s.cancel} onClick={onCancel}>
          Yes
        </Button>
        <Button className={s.delete} variant="solid" onClick={handleCloseModal}>
          No
        </Button>
      </div>
    </div>
  );
};
